import { DataGrid, StatusBadge } from '@ijoin/components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { GridColDef } from '@mui/x-data-grid';

import { LinkButton } from './LinkButton';
import { QueueChartLegend } from './QueueChartLegend';
import {
  HEALTH_LABEL,
  HEALTH_SEVERITY,
  IntegrationSeries,
  IntegrationSummaryRow
} from './queues.utils';

type BucketSeries = { data: number[]; label: string; stack: string };

type Props = {
  defers: { labels: string[]; series: BucketSeries[] };
  grouped: { series: IntegrationSeries[]; timestamps: Date[] };
  hits: { labels: string[]; series: BucketSeries[] };
  isLoading: boolean;
  missesByReason: { labels: string[]; series: BucketSeries[] };
  summaryRows: IntegrationSummaryRow[];
};

const summaryColumns: GridColDef<IntegrationSummaryRow>[] = [
  {
    field: 'health',
    flex: 0.75,
    headerName: 'Health',
    renderCell: params => (
      <Tooltip arrow title={params.row.hint}>
        <span>
          <StatusBadge severity={HEALTH_SEVERITY[params.value]}>
            {HEALTH_LABEL[params.value]}
          </StatusBadge>
        </span>
      </Tooltip>
    ),
    renderHeader: () => (
      <Stack alignItems='center' direction='row'>
        <span>Health</span>
        <Tooltip
          arrow
          title='Healthy: within limits. Pressure: ≥75% saturation with backlog. Critical: ≥90% saturation with large backlog. Idle: queue holds messages but consumer is not pulling.'>
          <IconButton>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  },
  {
    field: 'integrationName',
    flex: 2,
    headerName: 'Integration',
    renderCell: params => (
      <LinkButton
        params={{ integrationId: params.row.integrationId }}
        to='/integrations/$integrationId'
        variant='text'>
        {params.value}
      </LinkButton>
    )
  },
  {
    align: 'right',
    field: 'queueDepth',
    flex: 0.75,
    headerAlign: 'right',
    headerName: 'Depth',
    renderHeader: () => (
      <Stack alignItems='center' direction='row'>
        <span>Depth</span>
        <Tooltip
          arrow
          title='Outstanding messages for this integration (visible + in-flight + deferred). Tracked in Redis: incremented on send, decremented on handler completion.'>
          <IconButton>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  },
  {
    align: 'right',
    field: 'inFlight',
    flex: 0.75,
    headerAlign: 'right',
    headerName: 'In Flight',
    renderHeader: () => (
      <Stack alignItems='center' direction='row'>
        <span>In Flight</span>
        <Tooltip
          arrow
          title='Messages this integration has in active processing right now.'>
          <IconButton>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  },
  {
    align: 'right',
    field: 'cap',
    flex: 0.75,
    headerAlign: 'right',
    headerName: 'Cap / SOAP',
    renderCell: params =>
      params.row.parallelCalls
        ? `${params.value} / ${params.row.parallelCalls}`
        : `${params.value}`,
    renderHeader: () => (
      <Stack alignItems='center' direction='row'>
        <span>Cap / SOAP</span>
        <Tooltip
          arrow
          title='Left: rateLimitBatchSize (gate cap, max in-flight messages). Right: rateLimitMaxParallelCalls (SOAP throttle, max concurrent API calls).'>
          <IconButton>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  },
  {
    align: 'right',
    field: 'saturationPct',
    flex: 0.75,
    headerAlign: 'right',
    headerName: 'Saturation',
    renderHeader: () => (
      <Stack alignItems='center' direction='row'>
        <span>Saturation</span>
        <Tooltip arrow title='inFlight ÷ cap. 100% means pegged at the cap.'>
          <IconButton>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
    valueFormatter: value => `${value}%`
  },
  {
    align: 'right',
    field: 'avgMs',
    flex: 0.75,
    headerAlign: 'right',
    headerName: 'Avg ms',
    renderCell: params =>
      params.row.isSlowOutlier ? (
        <StatusBadge severity='error'>{`${params.value} ms`}</StatusBadge>
      ) : (
        `${params.value} ms`
      ),
    renderHeader: () => (
      <Stack alignItems='center' direction='row'>
        <span>Avg ms</span>
        <Tooltip
          arrow
          title='Average per-message processing time over the selected window. Highlighted red when at least 2× the median across active integrations on this queue.'>
          <IconButton>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  }
];

export const QueueGatingActivitySection = ({
  defers,
  grouped,
  hits,
  isLoading,
  missesByReason,
  summaryRows
}: Props) => (
  <Card sx={{ p: 1 }}>
    <CardContent>
      <Stack alignItems='center' direction='row' sx={{ mb: 2 }}>
        <Typography variant='subtitle1'>
          Gating Activity by Integration
        </Typography>
        <Tooltip
          arrow
          title='Per-integration view of the rate-limit gate. Only sampled when an integration is actively gating; quiet integrations may not appear here.'>
          <IconButton>
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {isLoading ? (
        <Alert severity='info'>Loading samples...</Alert>
      ) : grouped.series.length === 0 ? (
        <Alert severity='info'>
          No gate sampling data in the selected time range. Samples are recorded
          only when an integration is actively gating; this queue may not be
          gated, or has been idle.
        </Alert>
      ) : (
        <Stack spacing={3}>
          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                Active Integrations (latest sample)
              </Typography>
              <Tooltip
                arrow
                title='Most recent sample per integration. Sorted by saturation so pegged integrations float to the top.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <DataGrid
              columns={summaryColumns}
              disableColumnMenu
              disableRowSelectionOnClick
              getRowId={row => row.integrationId}
              hideFooterPagination
              initialState={{
                sorting: {
                  sortModel: [{ field: 'saturationPct', sort: 'desc' }]
                }
              }}
              rows={summaryRows}
              showColumnMenu
              slotProps={{
                root: {
                  'data-testid': 'integrationSummaryTable'
                }
              }}
            />
          </div>

          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                Messages in Queue per Integration
              </Typography>
              <Tooltip
                arrow
                title='Outstanding messages per integration over time, from Redis depth counters. Per-integration view that complements the SQS-side total backlog above.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <LineChart
              data-testid='queueDepthChart'
              height={320}
              series={grouped.series.map(s => ({
                connectNulls: true,
                data: s.queueDepth,
                label: s.integrationName,
                showMark: false
              }))}
              slots={{ legend: QueueChartLegend }}
              xAxis={[{ data: grouped.timestamps, scaleType: 'time' }]}
            />
          </div>

          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                In Flight per Integration (rate limit cap shown in legend)
              </Typography>
              <Tooltip
                arrow
                title='Concurrent in-flight messages per integration. The cap (rateLimitBatchSize) is shown in each legend entry.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <LineChart
              data-testid='inFlightChart'
              height={320}
              series={grouped.series.map(s => ({
                connectNulls: true,
                data: s.inFlight,
                label: `${s.integrationName} (cap=${s.latestBatchSize})`,
                showMark: false
              }))}
              slots={{ legend: QueueChartLegend }}
              xAxis={[{ data: grouped.timestamps, scaleType: 'time' }]}
            />
          </div>

          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                Saturation % (inFlight / cap, 100% = pegged)
              </Typography>
              <Tooltip
                arrow
                title='How close each integration is to its rate-limit cap. Sustained 100% means raise the cap or speed up downstream calls.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <LineChart
              data-testid='saturationChart'
              height={320}
              series={grouped.series.map(s => ({
                connectNulls: true,
                data: s.saturationPct,
                label: s.integrationName,
                showMark: false,
                valueFormatter: (v: null | number) =>
                  v === null ? '' : `${v}%`
              }))}
              slots={{ legend: QueueChartLegend }}
              xAxis={[{ data: grouped.timestamps, scaleType: 'time' }]}
              yAxis={[{ max: 100, min: 0 }]}
            />
          </div>

          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                Avg Processing Time per Integration (ms)
              </Typography>
              <Tooltip
                arrow
                title='Average per-message processing time per 30s sample window. Climbing trends or single integrations far above the others usually mean a slow downstream API.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <LineChart
              data-testid='avgProcMsChart'
              height={320}
              series={grouped.series.map(s => ({
                connectNulls: true,
                data: s.avgProcMs,
                label: s.integrationName,
                showMark: false,
                valueFormatter: (v: null | number) =>
                  v === null ? '' : `${v} ms`
              }))}
              slots={{ legend: QueueChartLegend }}
              xAxis={[{ data: grouped.timestamps, scaleType: 'time' }]}
            />
          </div>

          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                Hits per Minute (stacked by integration)
              </Typography>
              <Tooltip
                arrow
                title='Messages that successfully passed through the gate. Higher bars mean throughput; flat bars at the cap mean sustained pressure.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <BarChart
              data-testid='hitsChart'
              height={280}
              series={hits.series}
              slots={{ legend: QueueChartLegend }}
              xAxis={[{ data: hits.labels, scaleType: 'band' }]}
            />
          </div>

          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                Defers per Minute (stacked by integration)
              </Typography>
              <Tooltip
                arrow
                title='Messages re-queued because the gate was at cap. Persistent defers indicate the integration is throttled.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <BarChart
              data-testid='defersChart'
              height={280}
              series={defers.series}
              slots={{ legend: QueueChartLegend }}
              xAxis={[{ data: defers.labels, scaleType: 'band' }]}
            />
          </div>

          <div>
            <Stack alignItems='center' direction='row' sx={{ mb: 1 }}>
              <Typography color='text.secondary' variant='overline'>
                Misses by Rejection Reason (stacked, all integrations)
              </Typography>
              <Tooltip
                arrow
                title='Why the gate rejected acquisition. Total cap = at rateLimitBatchSize globally; Queue cap = per-(integration, queue) share full; Instance cap = this Node process is at its share of the integration cap. Heavy "Instance cap" without "Total cap" means per-process fairness is rebalancing the workload across hosts.'>
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <BarChart
              data-testid='missesByReasonChart'
              height={280}
              series={missesByReason.series}
              slots={{ legend: QueueChartLegend }}
              xAxis={[{ data: missesByReason.labels, scaleType: 'band' }]}
            />
          </div>
        </Stack>
      )}
    </CardContent>
  </Card>
);
